import { useState } from "react";
import { Table, Tag } from "antd";
import { FileText } from "lucide-react";
import type { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import { useCISList } from "../../../hooks/api/payslips/hooks";

const formatCurrency = (v: any) =>
  v != null ? `£${Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2 })}` : "—";

const formatDate = (v: any) =>
  v ? dayjs(v).format("DD MMM YYYY") : "—";

const statusColor = (s: string) => {
  if (!s) return "default";
  const sl = s.toLowerCase();
  if (sl === "submitted" || sl === "complete" || sl === "filed") return "green";
  if (sl === "pending") return "orange";
  if (sl === "failed" || sl === "error") return "red";
  return "default";
};

const CISPage = () => {
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data: res, isLoading } = useCISList({ page, limit });
  const records: any[] = (res?.data as any) ?? [];
  const total = Number(res?.extra?.total ?? 0);

  // Derive columns from first record keys
  const knownColumns: ColumnType<any>[] = [
    {
      title: "Worker",
      key: "worker",
      render: (_: any, r: any) =>
        r.worker_name || r.worker || r.name || r.employee_name || "—",
      width: 180,
    },
    {
      title: "Contractor / Subcontractor",
      key: "contractor",
      render: (_: any, r: any) =>
        r.contractor_name || r.subcontractor_name || r.company_name || "—",
      width: 200,
    },
    {
      title: "Period",
      key: "period",
      render: (_: any, r: any) =>
        r.tax_period || r.period || (r.period_start ? `${formatDate(r.period_start)} – ${formatDate(r.period_end)}` : "—"),
      width: 160,
    },
    {
      title: "Gross Payment",
      key: "gross",
      render: (_: any, r: any) =>
        formatCurrency(r.gross_payment ?? r.gross_pay ?? r.gross_amount ?? r.gross),
      width: 130,
    },
    {
      title: "Deduction",
      key: "deduction",
      render: (_: any, r: any) =>
        formatCurrency(r.cis_deduction ?? r.tax_deducted ?? r.deduction ?? r.deductions),
      width: 120,
    },
    {
      title: "Net Payment",
      key: "net",
      render: (_: any, r: any) =>
        formatCurrency(r.net_payment ?? r.net_pay ?? r.net_amount ?? r.net),
      width: 120,
    },
    {
      title: "Status",
      key: "status",
      render: (_: any, r: any) =>
        r.status ? (
          <Tag color={statusColor(r.status)}>{String(r.status).toUpperCase()}</Tag>
        ) : (
          "—"
        ),
      width: 110,
    },
    {
      title: "UTR / Ref",
      key: "utr",
      render: (_: any, r: any) =>
        r.utr || r.utr_number || r.reference || r.ref || "—",
      width: 140,
    },
    {
      title: "Date",
      key: "date",
      render: (_: any, r: any) =>
        formatDate(r.submission_date ?? r.payment_date ?? r.created_at),
      width: 120,
    },
  ];

  return (
    <div className="p-6 min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <FileText size={20} className="text-slate-500" />
            <h1 className="text-xl font-semibold text-slate-800">CIS Returns</h1>
          </div>
          <p className="text-slate-500 text-sm mt-0.5">
            Construction Industry Scheme tax deduction records
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <Table
          dataSource={records}
          columns={knownColumns}
          rowKey={(r) => r.id ?? r._id ?? Math.random()}
          loading={isLoading}
          locale={{
            emptyText: (
              <div className="py-12 text-center text-slate-400">
                <FileText size={40} className="mx-auto mb-3 opacity-30" />
                <p className="font-medium">No CIS records found</p>
                <p className="text-sm mt-1">
                  CIS return data will appear here once records are submitted.
                </p>
              </div>
            ),
          }}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showTotal: (t) => `${t} records`,
            showSizeChanger: false,
            onChange: (p) => setPage(p),
          }}
          scroll={{ x: 1000 }}
          size="small"
        />
      </div>
    </div>
  );
};

export default CISPage;
